import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiArrowLeft, FiShield, FiUser, FiPlus, FiTrash2, FiX, FiSave, FiCheck } = FiIcons;

const ROLES = [
  { value: 'superuser', label: 'Superuser', color: 'bg-purple-100 text-purple-800' },
  { value: 'admin', label: 'Admin', color: 'bg-indigo-100 text-indigo-800' },
  { value: 'blocked', label: 'Blocked', color: 'bg-red-100 text-red-800' },
];

const ManageUsers = () => {
  const { user, isSuperuser } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({ email: '', name: '', role: 'admin' });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [inlineRoles, setInlineRoles] = useState({});
  const [inlineNames, setInlineNames] = useState({});
  const [inlineSaving, setInlineSaving] = useState({});
  const [inlineSaved, setInlineSaved] = useState({});

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/users', { credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load users');
      setUsers(data.users);
      const roles = {};
      const names = {};
      data.users.forEach(u => { roles[u.id] = u.role; names[u.id] = u.name; });
      setInlineRoles(roles);
      setInlineNames(names);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isSuperuser) loadUsers(); }, [isSuperuser]);

  const openAdd = () => {
    setEditingUser(null);
    setForm({ email: '', name: '', role: 'admin' });
    setFormError('');
    setIsModalOpen(true);
  };

  const closeModal = () => { setIsModalOpen(false); setEditingUser(null); setFormError(''); };

  const handleSave = async (e) => {
    e.preventDefault();
    const email = form.email.trim().toLowerCase();
    const name = form.name.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setFormError('Please enter a valid email address.'); return; }
    if (!name) { setFormError('Please enter a name.'); return; }
    setSaving(true);
    try {
      const method = editingUser ? 'PUT' : 'POST';
      const url = editingUser ? `/api/users?id=${editingUser.id}` : '/api/users';
      const res = await fetch(url, { method, credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, name, role: form.role }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Save failed');
      await loadUsers();
      closeModal();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleInlineSave = async (u) => {
    const newRole = inlineRoles[u.id] ?? u.role;
    const newName = (inlineNames[u.id] ?? u.name).trim();
    if (newRole === u.role && newName === u.name) return;
    if (!newName) { alert('Name cannot be empty.'); return; }
    setInlineSaving(s => ({ ...s, [u.id]: true }));
    try {
      const res = await fetch(`/api/users?id=${u.id}`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: u.email, name: newName, role: newRole }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setInlineSaved(s => ({ ...s, [u.id]: true }));
      setTimeout(() => setInlineSaved(s => ({ ...s, [u.id]: false })), 2000);
      await loadUsers();
    } catch (err) {
      alert(err.message);
    } finally {
      setInlineSaving(s => ({ ...s, [u.id]: false }));
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Remove ${u.email}?`)) return;
    try {
      const res = await fetch(`/api/users?id=${u.id}`, { method: 'DELETE', credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      await loadUsers();
    } catch (err) {
      alert(err.message);
    }
  };

  if (!isSuperuser) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
        <h1 className="text-2xl font-bold text-gray-900">Access Denied</h1>
        <p className="text-gray-600 mt-2">You do not have permission to view this page.</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <button onClick={() => navigate('/admin')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-8">
        <SafeIcon icon={FiArrowLeft} className="w-5 h-5" /> Back to Dashboard
      </button>

      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Manage Users</h1>
          <p className="text-gray-600 mt-1">Control who can access the admin backend</p>
        </div>
        <button onClick={openAdd} className="flex items-center gap-2 text-white px-4 py-2 rounded-lg font-medium transition-colors" style={{ backgroundColor: 'var(--brand-primary)' }}>
          <SafeIcon icon={FiPlus} /> Add User
        </button>
      </div>

      <div className="mb-6 bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-600">
        <strong>Roles:</strong> <span className="font-medium text-purple-700">Superuser</span> — full access including user management. <span className="font-medium" style={{ color: 'var(--brand-primary)' }}>Admin</span> — can add, edit and delete their own articles. <span className="font-medium text-red-600">Blocked</span> — suspended, cannot log in.
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex items-center gap-2">
          <SafeIcon icon={FiShield} className="w-5 h-5" style={{ color: 'var(--brand-primary)' }} />
          <h2 className="text-lg font-semibold text-gray-900">Users</h2>
          <span className="ml-auto text-sm text-gray-500">{users.length} user{users.length !== 1 ? 's' : ''}</span>
        </div>

        {loading ? (
          <div className="px-6 py-12 text-center text-gray-500">Loading...</div>
        ) : error ? (
          <div className="px-6 py-12 text-center text-red-500">{error}</div>
        ) : users.length === 0 ? (
          <div className="px-6 py-12 text-center text-gray-500">No users yet. Add one above.</div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {users.map(u => {
              const superuserCount = users.filter(x => x.role === 'superuser').length;
              const isLastSuperuser = u.role === 'superuser' && superuserCount <= 1;
              const canDelete = !isLastSuperuser;
              const deleteTitle = isLastSuperuser
                ? 'Cannot delete — this is the only superuser'
                : `Remove ${u.email}`;
              return (
                <li key={u.id} className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--brand-primary-light)' }}>
                      <SafeIcon icon={FiUser} className="w-5 h-5" style={{ color: 'var(--brand-primary)' }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500 truncate">{u.email}</p>
                      {u.isCurrentUser && <p className="text-xs font-medium" style={{ color: 'var(--brand-primary)' }}>You</p>}
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button
                        onClick={() => canDelete && handleDelete(u)}
                        disabled={!canDelete}
                        title={deleteTitle}
                        className="text-red-500 hover:text-red-700 p-1 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <input
                      type="text"
                      value={inlineNames[u.id] ?? u.name}
                      onChange={e => setInlineNames(n => ({ ...n, [u.id]: e.target.value }))}
                      placeholder="Full name"
                      className="text-sm border border-gray-300 rounded-md px-2 py-1 flex-1 min-w-[140px] focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                    <select
                      value={inlineRoles[u.id] || u.role}
                      onChange={e => setInlineRoles(r => ({ ...r, [u.id]: e.target.value }))}
                      className="text-xs border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    >
                      {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                    <button
                      onClick={() => handleInlineSave(u)}
                      disabled={inlineSaving[u.id]}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-md font-medium transition-colors text-white disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ backgroundColor: 'var(--brand-primary)' }}
                    >
                      {inlineSaved[u.id] ? <SafeIcon icon={FiCheck} className="w-3 h-3" /> : <SafeIcon icon={FiSave} className="w-3 h-3" />}
                      {inlineSaving[u.id] ? 'Saving...' : inlineSaved[u.id] ? 'Saved' : 'Save'}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">{editingUser ? 'Edit User' : 'Add User'}</h2>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600"><SafeIcon icon={FiX} className="w-6 h-6" /></button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full name</label>
                <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" placeholder="e.g. Giuseppe Arcuri" autoFocus />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email address</label>
                <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" placeholder="user@haringeylearns.ac.uk" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                  {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              {formError && <p className="text-sm text-red-600">{formError}</p>}
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={closeModal} className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={saving} className="flex items-center gap-2 px-4 py-2 text-sm text-white rounded-lg font-medium disabled:opacity-75" style={{ backgroundColor: 'var(--brand-primary)' }}>
                  <SafeIcon icon={FiSave} /> {saving ? 'Saving...' : editingUser ? 'Update' : 'Add User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageUsers;
